import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "../api/axios";

// Create the Context
export const ContactContext = createContext();

// ✅ Optional helper: format phone numbers safely
const formatPhone = (phone) => {
  if (!phone) return "";

  // Normalize - remove non-digits first
  const digits = phone.replace(/\D/g, "");

  // Support either with or without country code
  const match = digits.match(/^1?(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
};


// Provider Component
export const ContactProvider = ({ children }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Fetch public contact info
  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get("/api/public/contacts", { withCredentials: false });
      if (response.data?.success) {
        setContacts(response.data.data || []);
      } else {
        setContacts([]);
      }
    } catch (err) {
      console.error("🔥 Error fetching contacts:", err);
      setError("Failed to load contact info.");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Load contacts on first render (app-wide initialization)
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);


  // ✅ Optional helper: get a contact by name (useful for Larry/Jason lookups)
  const getContactByName = (name) => {
    return contacts.find(
      (c) => c.name?.toLowerCase() === name?.toLowerCase()
    );
  };

  // ✅ Context value
  const value = {
    contacts,
    loading,
    error,
    refreshContacts: fetchContacts, // can be called manually if admin updates
    formatPhone,
    getContactByName,
  };

  return (
    <ContactContext.Provider value={value}>
      {children}
    </ContactContext.Provider>
  );
};
